import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import axios from 'axios';

import { Crypter } from '../../Functions/Security/Crypter';

import Data from '../../Data/Data.json';



export interface HttpOnlySessionUser {

  coduti?: string;

  nomuti?: string;

  codage?: string;

  agence?: string;

  codgroup?: string;

  group?: string;

  datjour?: string;

  CaisseUser?: string;

  Role?: string;

  role?: string;

  Authenticated?: boolean;

  MfaVerified?: boolean;

  ExpiresAt?: string;

  [key: string]: unknown;

}



interface SessionContextValue {

  user: HttpOnlySessionUser | null;

  loading: boolean;

  refreshSession: () => Promise<boolean>;

  signOut: () => Promise<void>;

  signIn: (payload?: { authState?: HttpOnlySessionUser }) => boolean;

}


const sessionEndpoint = Data.ApiUrl.session;

const logoutEndpoint = Data.ApiUrl.authLogout;

const SessionContext = createContext<SessionContextValue | null>(null);



const logAuth = (message: string, details?: Record<string, unknown>) => {

  console.info(``);

};



function decodeSession(response: any): HttpOnlySessionUser | null {

  try {

    const raw = typeof response?.data === 'string' ? response.data : JSON.stringify(response?.data);

    const decoded = Crypter.decrypt(raw);

    const parsed = JSON.parse(decoded);

    const value = parsed?.Value ?? parsed;

    const session = value?.Result ?? value;

    return normalizeSession(session);

  } catch {

    const value = response?.data?.Value ?? response?.data;

    const session = value?.Result ?? value;

    return normalizeSession(session);

  }

}



function normalizeSession(session: any): HttpOnlySessionUser | null {


  if (session?.Authenticated !== true) {

    return null;

  }



  const normalized = {

    ...session,

    coduti: session.coduti ?? session.CodUti ?? session.CODUTI,

    nomuti: session.nomuti ?? session.NomUti ?? session.NOMUTI,

    codage: session.codage ?? session.Codage ?? session.CODAGE,

    agence: session.agence ?? session.Agence ?? session.AGENCE,

    role: String(session.role ?? session.Role ?? '').trim(),

    codgroup: session.codgroup ?? session.CodGroup ?? session.CODGROUP ?? session.Role?.trim(),

    group: session.group ?? session.Group ?? session.GROUP ?? session.Role?.trim(),

    datjour: session.datjour ?? session.DatJour ?? session.DATJOUR,

    CaisseUser: session.CaisseUser ?? session.CAISSEUSER,

  };


  return normalized;

}



export function SessionProvider({ children }: { children: React.ReactNode }) {

  const [user, setUser] = useState<HttpOnlySessionUser | null>(null);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const code = user?.coduti ?? '';
    if (code) {
      localStorage.setItem('codUti', code);
      sessionStorage.setItem('codUti', code);
    } else {
      localStorage.removeItem('codUti');
      sessionStorage.removeItem('codUti');
    }
  }, [user]);



  const refreshSession = async () => {

    logAuth('GET /Authentification/session start', {

      endpoint: sessionEndpoint,

      withCredentials: true,

      cookieVisibleToJavaScript: document.cookie.includes('AuthToken='),

    });

    try {

      const response = await axios.get(sessionEndpoint, { withCredentials: true });

      const sessionUser = decodeSession(response);

      setUser((currentUser) => {

        if (!currentUser || !sessionUser) return sessionUser;



        const mergedUser = { ...currentUser };

        Object.entries(sessionUser).forEach(([key, value]) => {

          if (value !== null && value !== undefined && value !== '') {

            mergedUser[key] = value;

          }

        });

        return mergedUser;

      });

      logAuth('GET /Authentification/session response', {

        httpStatus: response.status,

        authenticated: Boolean(sessionUser),

        userCode: sessionUser?.CodUti ?? sessionUser?.coduti ?? null,

        role: sessionUser?.Role ?? null,

        mfaVerified: sessionUser?.MfaVerified ?? null,

      });

      return Boolean(sessionUser);

    } catch (error: any) {

      logAuth('GET /Authentification/session failed', {

        httpStatus: error?.response?.status ?? null,

        responseDataType: typeof error?.response?.data,

        networkRequestCreated: Boolean(error?.request),

        message: error?.message ?? 'unknown error',

        cookieVisibleToJavaScript: document.cookie.includes('AuthToken='),

      });

      setUser(null);

      return false;

    } finally {

      setLoading(false);

    }

  };



  const signOut = async () => {

    try {

      await axios.post(logoutEndpoint, undefined, { withCredentials: true });

    } finally {

      setUser(null);

    }

  };



  const signIn = (payload?: { authState?: HttpOnlySessionUser }) => {

    if (payload?.authState) setUser(payload.authState);

    return true;

  };



  useEffect(() => {

    const handleSessionExpired = () => setUser(null);

    window.addEventListener('auth:expired', handleSessionExpired);



    if (window.location.pathname.toLowerCase() !== '/login') {

      refreshSession();

    } else {

      setLoading(false);

    }



    return () => window.removeEventListener('auth:expired', handleSessionExpired);

  }, []);



  const value = useMemo(() => ({ user, loading, refreshSession, signOut, signIn }), [user, loading]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;

}



function useSessionContext() {

  const context = useContext(SessionContext);

  if (!context) throw new Error('SessionProvider is required.');

  return context;

}



export function useSession() {

  return useSessionContext();

}



export function useAuthUser() {

  const { user } = useSessionContext();

  return useCallback(() => user, [user]);

}



export function useAuthHeader() {

  return useCallback(() => '', []);

}



export function useSignIn() {

  return useSessionContext().signIn;

}



export function useSignOut() {

  return useSessionContext().signOut;

}



export function useIsAuthenticated() {

  const { user, loading } = useSessionContext();

  return () => !loading && Boolean(user);

}



export function RequireAuth({ children, loginPath }: { children: React.ReactNode; loginPath: string }) {

  const { user, loading } = useSessionContext();

  if (loading) return null;

  if (!user) {

    window.location.replace(loginPath);

    return null;

  }



  // ---- Garde MFA -----------------------------------------------------------

  // Tant que la session serveur indique MfaVerified === false, l'utilisateur

  // n'est authentifié qu'à moitié : il DOIT terminer l'activation ou la

  // vérification de son MFA. Même s'il modifie l'URL à la main (ex: "/"),

  // on le renvoie systématiquement vers la page MFA appropriée.

  const path = window.location.pathname.toLowerCase();

  const onMfaPage = path.startsWith('/mfa-activation') || path.startsWith('/mfa-verify');

  if (user.MfaVerified === false && onMfaPage) {
    const mfaBase = '/' + path.split('/')[1];
    if (path !== mfaBase) {
      window.location.replace(mfaBase);
      return null;
    }
  }

  if (user.MfaVerified === false && !onMfaPage) {

    logAuth('MFA guard: session not fully verified, redirecting to login', {

      from: window.location.pathname,

      to: loginPath,

    });

    sessionStorage.removeItem('mfaRequired');
    sessionStorage.removeItem('mfaConfigured');
    sessionStorage.removeItem('requiresSetup');
    sessionStorage.removeItem('assignedMfaMethod');

    window.location.replace(loginPath);

    return null;

  }



  return <>{children}</>;

}



export function AuthProvider({ children }: { children: React.ReactNode }) {

  return <SessionProvider>{children}</SessionProvider>;

}